/* eslint-disable react-refresh/only-export-components, prefer-const */

import dayjs from 'dayjs';
import range from 'lodash/range';
import sortedIndexBy from 'lodash/sortedIndexBy';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import weekday from 'dayjs/plugin/weekday';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import minMax from 'dayjs/plugin/minMax';
import { ProductType } from '../../types/shared';
import { getPremarketSymbols } from '../hiro';

export const MILLISECONDS_PER_DAY = 1_000 * 60 * 60 * 24;
export const MILLISECONDS_PER_HOUR = 1_000 * 60 * 60;

export const NYSE_OPENING_DST_UTC_HOUR = 13;
export const NYSE_OPENING_STD_UTC_HOUR = 14;
export const NYSE_OPENING_ET_HOUR = 9;
export const NYSE_OPENING_ET_MINUTES = 30;

export const NYSE_CLOSING_DST_UTC_HOUR = 20;
export const NYSE_CLOSING_STD_UTC_HOUR = 21;
export const NYSE_CLOSING_ET_HOUR = 16;

export const [SUN, MON, TUES, WED, THURS, FRI, SAT] = range(0, 7);

export function configureDayjs() {
  dayjs.extend(isBetween);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(weekday);
  dayjs.extend(advancedFormat);
  dayjs.extend(dayOfYear);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(minMax);
}

configureDayjs();

export { dayjs };

export const ET = 'America/New_York';

export const getAllTimezones = (): string[] => {
  return Intl.supportedValuesOf('timeZone');
};

const SEARCH_LIMIT = 7;
export const getCurrentDate = (tz = ET): dayjs.Dayjs => dayjs().tz(tz);

export const isBusinessDay = (timestamp = dayjs().tz(ET)) => {
  return timestamp.day() > 0 && timestamp.day() < 6;
};

export const ET_UTC_OFFSET_MS = dayjs().tz(ET).utcOffset() * 60 * 1_000;

export const stockMarketOpen = (date = dayjs()) =>
  date.tz(ET).set('hour', 9).set('minute', 30).set('second', 0).millisecond(0);

export const stockMarketClose = (date = dayjs()) =>
  date.tz(ET).set('hour', 16).set('minute', 0).set('second', 0).millisecond(0);

export const preMarketOpen = (date = getCurrentDate()) =>
  date.set('hour', 0).set('minute', 0).set('second', 0).millisecond(0);

export const shouldShowWaitingForMarketOpen = () => {
  const timestamp = dayjs().tz(ET);
  return isMarketOpenOnDate(timestamp) && timeToMarketOpenMs() > 0;
};

export const timeToMarketOpenMs = (timestamp = dayjs().tz(ET)) => {
  // negative if market already open
  return stockMarketOpen(timestamp).diff(timestamp);
};

export const timeToPreMarketOpenMs = (timestamp = dayjs().tz(ET)) => {
  // negative if premarket already open
  // technically with a premarket at midnight this will always be negative
  // but let's keep this in here for now in case we change the premarket start time
  return preMarketOpen(timestamp).diff(timestamp);
};

export const isPreMarket = (date: dayjs.Dayjs = getCurrentDate()): boolean => {
  return (
    isMarketOpenOnDate(date) &&
    timeToMarketOpenMs(date) > 0 &&
    timeToPreMarketOpenMs(date) <= 0
  );
};

export const isPostMarket = (timestamp: dayjs.Dayjs = getCurrentDate()) => {
  return (
    isMarketOpenOnDate(timestamp) &&
    timestamp.isAfter(stockMarketClose(timestamp))
  );
};

export const isOutsideTradingHours = (
  timestamp: dayjs.Dayjs = getCurrentDate(),
) => isPreMarket(timestamp) || isPostMarket(timestamp);

export const isMarketOpen = () => {
  const now = dayjs().tz(ET);
  return (
    isMarketOpenOnDate() &&
    now.isBetween(stockMarketOpen(), stockMarketClose(), null, '[]') // []' means inclusive
  );
};

export const getLastMarketClose = (
  timestamp: dayjs.Dayjs = getCurrentDate(),
) => {
  const todayClose = stockMarketClose(timestamp);

  // If we're before timestamp's close, get previous business day's close
  if (timestamp.isBefore(todayClose) || !isMarketOpenOnDate(timestamp)) {
    const prevDay = prevBusinessDayOpenMarket(timestamp);
    return stockMarketClose(prevDay);
  }

  return todayClose;
};

export const getQueryDate = (allowPreOpen = false, tz = ET): dayjs.Dayjs => {
  // if market has already opened today, it will return today (even if after 4pm est)
  // otherwise, it will get the last day the market was open accounting for market holidays + weekends
  let date = getCurrentDate(tz);
  if (isMarketOpenOnDate(date)) {
    if (allowPreOpen || date >= stockMarketOpen()) {
      return date;
    }
  }
  // if after cutoff for showing waiting for market then use today
  // otherwise use yesterday instead since market isn't open yet today
  date = date.subtract(1, 'day');
  while (!isMarketOpenOnDate(date)) {
    date = date.subtract(1, 'day');
  }
  return date;
};

// goes to the next (or previous) `numDays` market day from date `marketDate`
// if `numDays` is 0, returns original `marketDate`
//
// Params:
//  * marketDate - the date from which the following market days are calculated
//  * numDays - the number of market days to go forwards, if positive, or backwards, if negative
//
export const marketDayAdd = (
  marketDate: dayjs.Dayjs,
  numDays: number,
): dayjs.Dayjs => {
  const increment = numDays < 0 ? -1 : 1;

  for (let i = 0; i < Math.abs(numDays); ++i) {
    marketDate = marketDate.add(increment, 'days');
    while (!isMarketOpenOnDate(marketDate)) {
      marketDate = marketDate.add(increment, 'days');
    }
  }
  return marketDate;
};

export const getClosestBusinessDay = (): dayjs.Dayjs => {
  if (isBusinessDay()) {
    return getCurrentDate();
  }
  return nextBusinessDay();
};

export const getQueryDateFormatted = (allowPreOpen = false): string =>
  getDateFormatted(getQueryDate(allowPreOpen));

export const getFormattedDateFromUTC = (utcTimestamp: string | number) =>
  getDateFormatted(
    dayjs(
      typeof utcTimestamp === 'string' ? Number(utcTimestamp) : utcTimestamp,
    ).tz(ET),
  );

// data from https://www.nyse.com/markets/hours-calendars
// TODO: Add programmatic way to find market closure dates
export const CLOSURES = new Set([
  '2023-01-02',
  '2023-01-16',
  '2023-02-20',
  '2023-04-07',
  '2023-05-29',
  '2023-06-19',
  '2023-07-04',
  '2023-09-04',
  '2023-11-23',
  '2023-12-25',
  '2024-01-01',
  '2024-01-15',
  '2024-02-19',
  '2024-03-29',
  '2024-05-27',
  '2024-06-19',
  '2024-07-04',
  '2024-09-02',
  '2024-11-28',
  '2024-12-25',
  '2025-01-01',
  '2025-01-09',
  '2025-01-20',
  '2025-02-17',
  '2025-04-18',
  '2025-05-26',
  '2025-06-19',
  '2025-07-04',
  '2025-09-01',
  '2025-11-27',
  '2025-12-25',
  '2026-01-01',
  '2026-01-19',
  '2026-02-16',
  '2026-04-03',
  '2026-05-25',
  '2026-06-19',
  '2026-07-03',
  '2026-09-07',
  '2026-11-26',
  '2026-12-25',
]);

export const CLOSURE_DATES_AS_NUMS = Array.from(CLOSURES).map((closure) =>
  parseInt(closure.replace(/-/g, '')),
);
CLOSURE_DATES_AS_NUMS.sort();

export type DayjsInput =
  | string
  | number
  | Date
  | dayjs.Dayjs
  | null
  | undefined;

export const getDateFormatted = (date: DayjsInput): string => {
  return dayjs(date).format(dateFormat);
};

export const isMarketOpenOnDate = (date: dayjs.Dayjs = dayjs()): boolean => {
  return !(
    date.weekday() === 0 ||
    date.weekday() === 6 ||
    CLOSURES.has(getDateFormatted(date))
  );
};

export const isHolidayOnDateTuple = (
  year: number,
  month: number,
  date: number,
): boolean => {
  const dateAsNum = year * 10_000 + month * 100 + date;
  return (
    CLOSURE_DATES_AS_NUMS[sortedIndexBy(CLOSURE_DATES_AS_NUMS, dateAsNum)] ==
    dateAsNum
  );
};

export const isMarketOpenOnDateObj = (date: Date): boolean => {
  const weekday = date.getUTCDay();
  if (weekday === SUN || weekday === SAT) {
    return false;
  }
  return !isHolidayOnDateTuple(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
  );
};

export const spansNonMktDays = (a: dayjs.Dayjs, b: dayjs.Dayjs) => {
  let [lo, hi] = a.isBefore(b) ? [a, b] : [b, a];
  for (; hi.isAfter(lo); hi = hi.subtract(1, 'day')) {
    if (!isMarketOpenOnDate(hi)) {
      return true;
    }
  }
  return false;
};

export const prevBusinessDay = (timestamp = dayjs().tz(ET)) => {
  let currentDay = timestamp.clone();
  let loopIndex = 1;
  while (loopIndex < SEARCH_LIMIT) {
    currentDay = currentDay.subtract(1, 'day');

    if (isBusinessDay(currentDay)) {
      break;
    }
    loopIndex += 1;
  }

  return currentDay;
};

export const nextBusinessDay = (timestamp = dayjs().tz(ET)) => {
  return nextBusinessDayHolidays(dayjs(timestamp), false);
};

const nextBusinessDayHolidays = (
  timestamp: dayjs.Dayjs,
  includeHolidays: boolean,
) => {
  let currentDay = timestamp.clone();
  let loopIndex = 1;
  while (loopIndex < SEARCH_LIMIT) {
    currentDay = currentDay.add(1, 'day');
    if (
      (includeHolidays && isBusinessDay(currentDay)) ||
      (!includeHolidays && isMarketOpenOnDate(currentDay))
    ) {
      break;
    }
    loopIndex += 1;
  }

  return currentDay;
};

export const prevBusinessDayOpenMarket = (timestamp = dayjs().tz(ET)) => {
  const searchLimit = 7;
  let currentDay = timestamp.clone();
  let loopIndex = 1;
  while (loopIndex < searchLimit) {
    currentDay = prevBusinessDay(currentDay);

    if (isMarketOpenOnDate(currentDay)) {
      break;
    }
    loopIndex += 1;
  }

  return currentDay;
};

export const businessDaysSubtract = (
  timestamp = dayjs().tz(ET),
  numDays: number,
) => {
  let currentDay = timestamp.clone();
  let i;
  for (i = 0; i < numDays; i++) {
    currentDay = prevBusinessDay(currentDay);
  }
  return currentDay;
};

export const businessDaysAdd = (
  timestamp = dayjs().tz(ET),
  numDays: number,
) => {
  let currentDay = timestamp.clone();
  let i;
  for (i = 0; i < numDays; i++) {
    currentDay = nextBusinessDay(currentDay);
  }
  return currentDay;
};

const dateFormat = 'YYYY-MM-DD';

export const getDaysUntil = (
  timestamp1: string,
  timestamp2 = dayjs.utc(),
): number => {
  return dayjs.utc(timestamp1, dateFormat).diff(timestamp2, 'days');
};

/** Whole ET calendar days from today until `day`; 0 once it has passed. */
export const etCalendarDaysUntil = (day: dayjs.Dayjs): number =>
  Math.max(
    0,
    day.tz(ET).startOf('day').diff(dayjs().tz(ET).startOf('day'), 'day'),
  );

export const roundToStartOfDay = (timestamp: number) => {
  return dayjs(timestamp).startOf('day').valueOf();
};

export function calcOffsetMS(tz: string, time: number = Date.now()) {
  const utcOffsetMinutes = dayjs.tz(time, tz).utcOffset();
  return utcOffsetMinutes * 60 * 1_000;
}

export const getLastMonday = () => getDateFormatted(dayjs().day(1));

// TODO: Read preferred date format from User settings
export const getYMD = (tz: string, epoch_millis: number | string) =>
  getDateFormatted(dayjs(epoch_millis).tz(tz));

// Returns format in Aug 16, 2018 8:02 PM
export const getDateAndTime = (timestamp: string) =>
  dayjs(timestamp).format('lll');

// Days in our model tables come in as UTC midnight timestamps.  So they must
// be displayed as UTC to get the correct date.
export const getUtcYMD = (epoch_millis: number | string) =>
  getYMD('UTC', epoch_millis);

// Formats timestamp to August 16, 2018
export const formatToLLDate = (timestamp: string) =>
  dayjs(timestamp).format('LL');

export const getMostRecentBusinessDay = () => {
  if (isBusinessDay()) {
    return getCurrentDate();
  }
  return prevBusinessDay();
};

export const getQueryDateForSymbol = (
  symbol: string | undefined,
  productType: ProductType | null | undefined,
  tz?: string,
) => {
  return getQueryDate(getPremarketSymbols(productType).has(symbol ?? ''), tz);
};

export const isLatestTradingDay = (date: dayjs.Dayjs) => {
  // getQueryDate returns in ET timezone so make sure the passed in date tz matches
  return date.tz(ET).isSame(getQueryDate(true, ET), 'day');
};

export const getTzOffsetMs = (tz: string) => {
  const browserOffset = dayjs().utcOffset(); // in mins
  const userTzOffset = dayjs().tz(tz).utcOffset(); // in mins
  return (userTzOffset - browserOffset) * 60 * 1_000;
};

export const generateExpirationTimestamps = (
  startDate: dayjs.Dayjs = dayjs().tz(ET),
  yearsAhead: number = 3,
): string[] => {
  // Always work in ET timezone
  let currentDate = startDate.tz(ET).startOf('day');

  const endDate = currentDate.add(yearsAhead, 'year');
  const marketDays: string[] = [];

  while (currentDate.isBefore(endDate)) {
    if (isMarketOpenOnDate(currentDate)) {
      // Create timestamp for 4 PM ET for this date
      const timestamp = dayjs
        .tz(`${currentDate.format('YYYY-MM-DD')} 16:00:00`, ET)
        .valueOf();
      marketDays.push(timestamp.toString());
    }
    currentDate = currentDate.add(1, 'day');
  }

  return marketDays;
};

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));
